import {
	AlertDialog,
	Dialog,
	IconButton,
	MultiSelect,
	MultiSelectItem,
	renderReactDialog,
	Toolbar
} from "client/Components";
import {
	applyMembersRoleAssignmentChanges,
	CreateSharePointMemberRoleAssignments,
	findMembers,
	getSitePermissionsViewModel,
	SharePointMemberChangesForRoleAssignments,
	UpdateSharePointMemberRoleAssignments
} from "client/Api/Documents/SharePoint/site";
import { SharePointMember, SharePointPeoplePickerItemModel } from "client/Api/Documents/SharePoint/sharePointSharing";
import {
	ReactElement,
	useCallback,
	useEffect,
	useState
} from "react";
import { intersection } from "client_legacy/ndaw/arrayHelpers";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { localize } from "client_legacy/ndaw/culture";
import { ClientValidationErrorModel } from "client/Api/common";
import { HttpRequestError } from "client/Api/httpClient";
import styles from "./SitePermissionsDialog.module.less";
import { PeoplePicker, attachToPeoplePicker } from "../PeoplePicker";
import {
	createValidationConfiguration,
	IValidationConfiguration,
	InputValidationResult,
	InputValidationResultKind
} from "client/Validation";
import { isNotNullNorUndefined } from "client/Validation/rules";
import { IObjectInputAttachable } from "client/Forms/attachable";
import { IFormHandler, useFormHandler } from "client/Forms";
import { generateTrackingId, setTrackingId } from "client/Misc/utils";
import { AlertManager, useAlerts, useUnmountedCancellation } from "client/Hooks";
import classNames from "classnames";

type ExistingAssignmentEntry = {
	clientId: number;
	isNew: false;
	member: SharePointMember;
	canBeChanged: boolean;
	roleIds: readonly number[];
	originalRoleIds: readonly number[];
};

type NewAssignmentEntry = {
	clientId: number;
	roleIds: readonly number[];
	isNew: true;
	member: SharePointPeoplePickerItemModel | undefined;
};

type AssignmentEntry = ExistingAssignmentEntry | NewAssignmentEntry;

type Model = {
	assignments: readonly AssignmentEntry[];
};

const validationConfiguration: IValidationConfiguration<Model> = createValidationConfiguration<Model>();

validationConfiguration.forCollection("assignments")
	.forItems(x => x.clientId.toString())
	.forObject("member")
	.add(
		isNotNullNorUndefined()
	);

let _clientIdGenerator: number = 1;

export type SitePermissionsDialogProps = {
	siteUrl: string;
	onClose: () => void;
};

export const SitePermissionsDialog = (props: SitePermissionsDialogProps): ReactElement => {
	const {
		siteUrl,
		onClose
	} = props;

	const [isLoading, setIsLoading] = useState<boolean>(true);
	const [hadErrorLoading, setHadErrorLoading] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);
	const [roles, setRoles] = useState<readonly MultiSelectItem<number>[]>([]);
	const [model, setModel] = useState<Model>({ assignments: [] });
	const [memberIdsOfRemovedAssignments, setMemberIdsOfRemovedAssignments] = useState<readonly number[]>([]);

	const globalCancellation: AbortSignal = useUnmountedCancellation();
	const alertManager: AlertManager = useAlerts();

	const formHandler: IFormHandler<Model> = useFormHandler(
		model,
		setModel,
		validationConfiguration
	);

	useEffect(
		() => {
			async function load(siteUrl: string, signal: AbortSignal): Promise<void> {
				setHadErrorLoading(false);
				setIsLoading(true);

				try {
					const { roles, rolesAssignments } = await getSitePermissionsViewModel(siteUrl, signal);

					setRoles(roles.map((x) => ({ value: x.id, text: x.name })));

					setModel({
						assignments: rolesAssignments.map(
							(x) => ({
								clientId: ++_clientIdGenerator,
								isNew: false,
								canBeChanged: x.canBeChanged,
								roleIds: x.roleDefinitionIds,
								originalRoleIds: x.roleDefinitionIds,
								member: x.member
							})
						)
					});

					setIsLoading(false);

				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					setHadErrorLoading(true);
				}
			}

			const cancellation = new AbortController();
			load(siteUrl, cancellation.signal);
			return () => cancellation.abort();
		},
		[
			formHandler.reducer,
			onClose,
			siteUrl
		]
	);

	const removeHandler = (assignment: AssignmentEntry): void => {
		formHandler.for("assignments")
			.reducer
			.remove(x => x.clientId === assignment.clientId);

		if (assignment.isNew === false) {
			setMemberIdsOfRemovedAssignments(x => [...x, assignment.member.id]);
		}
	};

	const addHandler = (): void => {
		formHandler.for("assignments")
			.reducer
			.push({
				isNew: true,
				roleIds: [],
				member: undefined,
				clientId: ++_clientIdGenerator
			});
	};

	const submitHandler = async (): Promise<void> => {
		if (isSaving) {
			return;
		}

		setIsSaving(true);

		const validationState: InputValidationResult = await formHandler.validation.validate();
		if (validationState.kind === InputValidationResultKind.Invalid)
		{
			setIsSaving(false);
			return;
		}

		try {
			const createdAssignments: CreateSharePointMemberRoleAssignments[] = [];
			const updatedAssignments: UpdateSharePointMemberRoleAssignments[] = [];

			for (const assignment of model.assignments) {
				if (assignment.isNew === true) {
					if (!assignment.member || assignment.roleIds.length === 0) {
						continue;
					}
					createdAssignments.push({
						selectedMember: assignment.member,
						roleDefinitionIds: assignment.roleIds
					});

				} else {
					const overlappingRoleIds: readonly number[] = intersection(
						assignment.roleIds,
						assignment.originalRoleIds
					);

					const hasChanges: boolean = overlappingRoleIds.length !== assignment.originalRoleIds.length
						|| overlappingRoleIds.length !== assignment.roleIds.length;

					if (!hasChanges) {
						continue;
					}

					updatedAssignments.push({
						id: assignment.member.id,
						roleDefinitionIds: assignment.roleIds
					});
				}
			}

			const hasChanges: boolean = createdAssignments.length > 0
				|| updatedAssignments.length > 0
				|| memberIdsOfRemovedAssignments.length > 0;

			if (!hasChanges) {
				onClose();
				return;
			}

			const changes: SharePointMemberChangesForRoleAssignments = {
				create: createdAssignments,
				update: updatedAssignments,
				"delete": memberIdsOfRemovedAssignments
			};

			await applyMembersRoleAssignmentChanges(
				siteUrl,
				changes,
				globalCancellation
			);

			alertManager.showSuccess(localize("_PermissionsHaveBeenChanged"));
			onClose();
		} catch (e) {
			let message: string = localize("_YouCantChangePermissions");

			try {
				if (e instanceof HttpRequestError && e.statusCode === 400) {
					const errorResponse: ClientValidationErrorModel = e.readJson<ClientValidationErrorModel>();
					message = errorResponse.errorMessage;
				}
			} finally {
				alertManager.showError(message);
			}

			setIsSaving(false);
		}
	};

	if (hadErrorLoading) {
		return (
			<AlertDialog
				title={localize("_UnknownError")}
				confirmText={localize("_Close")}
				onClose={onClose}
				trackingId="sp-site-permissions-loading-failed-dialog"
			>
				{localize("_ItIsCurrentlyNotPossibleToManageSitePermissions")}
			</AlertDialog>
		);
	}

	return (
		<Dialog
			trackingId="sp-site-permissions-dialog"
			hasBusyIndicator={isLoading || isSaving}
		>
			<Dialog.Header
				title={localize("_WhoHasAccess")}
				onClose={onClose}
			/>

			<Dialog.Body>
				{formHandler.for("assignments").value.length > 0 && (
					<div className={styles.container}>
						<ul className={styles.list}>
							{formHandler.for("assignments").map((x) => (
								<li key={x.value.clientId}>
									{ x.value.isNew === true
										? (
											<NewAssignment
												siteUrl={siteUrl}
												formHandler={x as IObjectInputAttachable<NewAssignmentEntry>}
												allRoles={roles}
												onRemove={() => removeHandler(x.value)}
											/>
										)
										: (
											<ExistingAssignment
												formHandler={x as IObjectInputAttachable<ExistingAssignmentEntry>}
												allRoles={roles}
												onRemove={() => removeHandler(x.value)}
											/>
										)
									}
								</li>
							))}
						</ul>
					</div>
				)}

				{formHandler.for("assignments").value.length === 0 && (
					<p className={styles.emptyText}>
						{localize("_SharePointPermissionsModalEmptyBody")}
					</p>
				)}

				<IconButton
					kind="dashed"
					icon="plus"
					onClick={addHandler}
					trackingId="add"
					width="fill"
				/>

			</Dialog.Body>

			<Dialog.Footer>
				<Toolbar>
					<Toolbar.Button
						kind="primary"
						text={localize("_Done")}
						onClick={submitHandler}
						trackingId="done"
					/>

					<Toolbar.Button
						kind="secondary"
						text={localize("_Close")}
						onClick={onClose}
						trackingId="close"
						responsiveBehavior="hide"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
SitePermissionsDialog.displayName = "SitePermissionsDialog";

type ExistingAssignmentProps = {
	formHandler: IObjectInputAttachable<ExistingAssignmentEntry>;
	allRoles: readonly MultiSelectItem<number>[];
	onRemove(): void;
};

const ExistingAssignment = (props: ExistingAssignmentProps): ReactElement => {
	const {
		formHandler,
		allRoles,
		onRemove,
	} = props;

	const canBeChanged: boolean = formHandler.for("canBeChanged").reducer.value;

	const trackingId: string = formHandler.reducer.path;

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.assignment}
		>
			<div
				className={classNames(
					styles.member,
					styles.member_name
				)}
			>
				{formHandler.for("member")
					.reducer
					.value
					.name
				}
			</div>

			<div className={styles.roles}>
				<MultiSelect
					{...formHandler.for("roleIds").attachToMultiSelect()}
					options={allRoles}
					isDisabled={!canBeChanged}
					menuTitle={localize("_Permissions")}
				/>
			</div>

			<IconButton
				kind="secondary"
				icon="minus"
				onClick={onRemove}
				isDisabled={!canBeChanged}
				trackingId={generateTrackingId(trackingId, "__remove")}
			/>
		</div>
	);
};
ExistingAssignment.displayName = "ExistingAssignment";

type NewAssignmentProps = {
	siteUrl: string;
	formHandler: IObjectInputAttachable<NewAssignmentEntry>;
	allRoles: readonly MultiSelectItem<number>[];
	onRemove(): void;
};

const NewAssignment = (props: NewAssignmentProps): ReactElement => {
	const {
		siteUrl,
		formHandler,
		allRoles,
		onRemove
	} = props;

	const findMembersHandler = useCallback(
		(
			searchTerm: string,
			signal: AbortSignal
		): Promise<readonly SharePointPeoplePickerItemModel[]> => {
			return findMembers(
				siteUrl,
				searchTerm,
				signal
			);
		},
		[siteUrl]
	);

	const trackingId: string = formHandler.reducer.path;

	return (
		<div
			{...setTrackingId(trackingId)}
			className={styles.assignment}
		>
			<div className={styles.member}>
				<PeoplePicker
					{...attachToPeoplePicker(formHandler.for("member"))}
					onFindMembers={findMembersHandler}
					autoFocus="focus"
					menuTitle={localize("_Member")}
				/>
			</div>

			<div className={styles.roles}>
				<MultiSelect
					{...formHandler.for("roleIds").attachToMultiSelect()}
					options={allRoles}
					menuTitle={localize("_Permissions")}
				/>
			</div>

			<IconButton
				kind="secondary"
				icon="minus"
				onClick={onRemove}
				trackingId={generateTrackingId(trackingId, "__remove")}
			/>
		</div>
	);
};
NewAssignment.displayName = "NewAssignment";

export function openSitePermissionsDialog(props: Omit<SitePermissionsDialogProps, "onClose">): void {
	renderReactDialog(
		(onClose) => (
			<SitePermissionsDialog
				{...props}
				onClose={onClose}
			/>
		)
	);
}
